import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Company, ContentProvider } from '@lighty';
import { EmitterService, StorageService, TranslateService } from '@services';
import { DataHelper } from '@helpers';

const FILTER_DATE_OPTIONS = (translateService) => {
    return [{
        label: translateService.instant('words.addition-date'),
        value: '-date',
    }, {
        label: 'A-Z',
        value: 'title'
    }, {
        label: 'Z-A',
        value: '-title'
    }]
};

@Component({
    selector: 'msc-shared-topbar-filters',
    templateUrl: './topbar-filters.component.html',
    styleUrls: ['./topbar-filters.component.scss']
})
export class SharedTopbarFiltersComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() type: string;
    @Input() color: string = '';
    @Input() query: string;
    @Input() mode: string;
    @Input() filters: any;
    @Input() activeFilters: boolean = false;
    @Input() buildFilters: EventEmitter<any> = new EventEmitter();
    //
    @Output() queryChange: EventEmitter<any> = new EventEmitter();
    @Output() modeChange: EventEmitter<any> = new EventEmitter();
    @Output() filtersChange: EventEmitter<any> = new EventEmitter();
    @Output() activeFiltersChange: EventEmitter<any> = new EventEmitter();
    @Output() collapsedChange: EventEmitter<boolean> = new EventEmitter();
    //
    //ocultar select en saved
    @Input() showAdvancedFilters: boolean = true;

    private me: any;
    private company: Company;
    public availableSkills: any[] = [];
    public availableTypes: any[] = [];
    public availableLanguages: any[] = [];
    public availableProviders: any[];
    public filtersModal: any;
    public slider: any;
    public tempSlider: any;
    public dates: any;
    public orderDate: any = '-date';
    public selectOrderOptions: any[] = [];

    // Status filter
    public statusOptions: any[] = [];
    @Input() statusCounts: { inProgress: number; completed: number; notStarted: number; total: number } = 
        { inProgress: 0, completed: 0, notStarted: 0, total: 0 };

    // Subscription filter: null = todos | 'subscribed' = inscritos | 'not_subscribed' = no inscritos
    public subscriptionFilter: string | null = null;

    public onSubscriptionFilterChange(value: string): void {
        // Toggle: si el mismo valor ya está activo, se desactiva
        this.subscriptionFilter = this.subscriptionFilter === value ? null : value;
        this.saveFilters();
    }

    public bookmarkActive: boolean = false;
    public collapsed: boolean = false;
    public mobileDrawerOpen: boolean = false;

    public openMobileDrawer(): void {
        this.mobileDrawerOpen = true;
    }

    public closeMobileDrawer(): void {
        this.mobileDrawerOpen = false;
    }
    public openSections: { [key: string]: boolean } = {
        skills: true,
        types: true,
        providers: true,
        langs: false,
        status: true,
        order: true,
        subscription: false
    };
    public expandedSections: { [key: string]: boolean } = {
        skills: false
    };

    public toggleSection(key: string): void {
        this.openSections[key] = !this.openSections[key];
    }

    public toggleCollapsed(): void {
        this.collapsed = !this.collapsed;
        this.collapsedChange.emit(this.collapsed);
    }

    constructor(
        private contentProvider: ContentProvider,
        private emitterService: EmitterService,
        private router: Router,
        private storageService: StorageService,
        private translateService: TranslateService
    ) { }

    /** NUEVO: filter new iu evitando el save de provedores e idioma checks  */
    onInlineMultiselectChange(option, filterName: string, optionsArray: any[]) {
        // 1. toggle en el objeto concreto
        const opt = optionsArray.find(o => o.value === option.value);

        // Manejar tanto isSelected como isChecked para compatibilidad
        if (filterName === 'providers') {
            opt.isChecked = !opt.isChecked;
            opt.isSelected = opt.isChecked;
        } else {
            opt.isSelected = !opt.isSelected;
        }

        // Al activar un proveedor, deseleccionar traject (Ruta de Aprendizaje no se combina con proveedor)
        if (filterName === 'providers' && opt.isChecked) {
            const trajectType = this.availableTypes.find(t => t.value === 'traject');
            if (trajectType && trajectType.isSelected) {
                trajectType.isSelected = false;
                if (trajectType.children) { this.getIterativeCheck(trajectType); }
            }
        }

        // 2. reconstruye filtro un array de valores
        const selectedValues = optionsArray
            .filter(o => filterName === 'providers' ? o.isChecked : o.isSelected)
            .map(o => o.value);

        // 3. merge y guarda (usa tu método ya existente)
        this.filters = DataHelper.merge(this.filters, { [filterName]: selectedValues });
        this.saveFilters();   // método existente que emite events y cachea
    }

    ///*

    ngOnInit(): void {
        this.me = this.storageService.get('me');
        this.company = this.storageService.get('company');

        this.selectOrderOptions = this.getSelectOrderOptions(FILTER_DATE_OPTIONS(this.translateService));

        this.getMeta();
        this.initSlider();
        this.initStatusOptions();

        this.buildFilters.subscribe((filters) => {
            this.activeFilters = true;
            this.activeFiltersChange.emit(this.activeFilters);
            DataHelper.merge(this.filters, filters);
            this.filtersChange.emit(this.filters);
        });

        // Escuchar evento de sincronización de filtros desde inline-filters-carousel
        this.emitterService.get('filter.sync.topbar').subscribe((data: any) => {
            this.syncFiltersFromCarousel(data);
        });
    }

    ngAfterViewInit(): void {
        const timeOut = setTimeout(() => {
            this.getStoredFilters();
            // Sincronizar los arrays visuales (availableSkills, availableProviders, etc.)
            // con los filtros recuperados del almacenamiento para que isSelected coincida
            // con los valores guardados
            this.syncFiltersWithInput();
            clearTimeout(timeOut);
        }, 150);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Si los filtros cambian desde el padre, sincronizamos los UI elements
        if (changes['filters'] && !changes['filters'].firstChange) {
            this.syncFiltersWithInput();
        }
        // Si los contadores de status cambian, actualizar las opciones
        if (changes['statusCounts'] && !changes['statusCounts'].firstChange) {
            this.updateStatusCounts(this.statusCounts);
        }
    }

    ngOnDestroy(): void {
        this.removeStoredFilters();
    }

    getOrderDate(): string {
        switch (this.orderDate) {
            case '-date':
                return this.translateService.instant('words.addition-date');
            case 'title':
                return 'A-Z';
            case '-title':
                return 'Z-A';
        }
    }

    getSelectOrderOptions(data) {
        return data.map((d, index) => {
            const id = index;
            const label = d.label;
            const value = d.value;
            const isSelected = d.label === this.getOrderDate();
            return { id, label, value, isSelected };
        });
    }

    getSubTypesOptions(data: any, key: string) {
        const subtypes = data[`${key}Types`];
        if (!subtypes) {
            return [];
        }

        return Object.keys(subtypes)
            .map((subtype) => ({
                id: `type-${key}-subtype-${subtype}`,
                label: this.translateService.instant(`words.course-type.${subtype}`),
                value: subtype,
                isSelected: this.filters[`${key}Types`]?.includes(subtype)
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    getTypesOptions(data: any) {
        const labelOverrides: { [key: string]: string } = {
            'course': this.translateService.instant('words.courses'),
            'traject': this.translateService.instant('words.journeys')
        };
        return Object.keys(data.contentTypes)
            .map((type) => {
                const value = type.split(/(?=[A-Z])/).join('_').toLowerCase();
                return {
                    id: `type-${type}`,
                    label: labelOverrides[value] ?? this.translateService.instant(`api.content-type.${value}`),
                    value,
                    // FUSIÓN: external_content sigue en el array para que sus subtypes
                    // se seleccionen correctamente (el backend los necesita).
                    // Se marca activo si el filtro incluye 'course' O 'external_content'.
                    // El HTML lo oculta con *ngIf para que el usuario solo vea "Cursos".
                    isSelected: (value === 'course' || value === 'external_content')
                        ? (this.filters.contentTypes?.includes('course') || this.filters.contentTypes?.includes('external_content'))
                        : this.filters.contentTypes?.includes(value),
                    children: this.getSubTypesOptions(data, type)
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    getProvidersOptions(providers) {
        const providerIds = this.filters?.providers ? this.filters.providers : [];
        const label = this.company.experience
            ? this.company.experience.displayName
                ? this.company.experience.displayName
                : this.company.experience.name
            : this.company.name;

        return providers.map(provider => ({
            label: provider.id === 'company' ? label : provider.name,
            value: provider.id,
            isChecked: providerIds.includes(provider.id)
        }));
    }

    getLanguagesOptions(languages) {
        const languageIds = this.filters?.langs ? this.filters.langs : this.me.learningLanguages.map(lang => lang.id);

        return languages.map((lang, idx) => ({
            id: idx,
            label: lang.id === 'undefined' ? this.translateService.instant('words.multilingual') : lang.name,
            value: lang.id,
            isSelected: languageIds.includes(lang.id)
        }));
    }

    private getSkillsOptions(skills: any[]): any[] {
        if (!skills || !skills.length) return [];
        const selectedIds = this.filters?.contentSkills || [];
        return skills.map(skill => ({
            label: skill.name,
            value: skill.id,
            isSelected: selectedIds.includes(skill.id)
        }));
    }

    private getMeta(): void {
        this.contentProvider.getMeta(this.type).subscribe((meta) => {
            this.availableSkills = this.getSkillsOptions(meta.contentSkills || []);
            this.availableTypes = this.getTypesOptions(meta);
            this.availableLanguages = this.getLanguagesOptions(meta.languages);
            this.availableProviders = this.getProvidersOptions(meta.providers);

            this.setFilters();
        });
    }

    private syncFiltersWithInput(): void {
        if (this.filters?.contentTypes) {
            const courseActive = this.filters.contentTypes.includes('course')
                || this.filters.contentTypes.includes('external_content');
            for (let type of this.availableTypes) {
                // FUSIÓN: course y external_content se sincronizan juntos.
                // Si el filtro tiene cualquiera de los dos, ambos se marcan activos
                // para que sus subtypes (children) queden correctamente seleccionados.
                if (type.value === 'course' || type.value === 'external_content') {
                    type.isSelected = courseActive;
                    if (type.children) { this.getIterativeCheck(type); }
                } else {
                    type.isSelected = this.filters.contentTypes.includes(type.value);
                }
            }
        }
        if (this.filters?.providers !== undefined) {
            for (let provider of this.availableProviders) {
                provider.isChecked = this.filters.providers ? this.filters.providers.includes(provider.value) : false;
                provider.isSelected = provider.isChecked;
            }
        }
        if (this.filters?.contentSkills !== undefined) {
            for (const skill of this.availableSkills) {
                skill.isSelected = this.filters.contentSkills ? this.filters.contentSkills.includes(skill.value) : false;
            }
        }
        // Actualizar el modal visual si hay filtros cargados
        if (this.filters?.providers !== undefined || this.filters?.contentSkills !== undefined) {
            this.setFilters();
        }
    }

    private syncFiltersFromCarousel(data: any): void {
        if (data.availableTypes) {
            // FUSIÓN: al recibir tipos del carrusel mantenemos todos los tipos incluyendo
            // external_content para que sus subtypes queden sincronizados correctamente.
            this.availableTypes = JSON.parse(JSON.stringify(data.availableTypes));
        }
        if (data.availableProviders) {
            this.availableProviders = data.availableProviders.map(provider => ({
                ...provider,
                isChecked: provider.isSelected
            }));
            this.setFilters();
        }
        if (data.availableSkills) {
            this.availableSkills = JSON.parse(JSON.stringify(data.availableSkills));
        }
        if (data.availableTypes || data.availableProviders || data.availableSkills) {
            this.saveFilters();
        }
    }

    private getStoredFilters(): void {
        const filters = this.storageService.getAllCache().filter((record: any) => {
            return record.key.match(this.type + '.filter.');
        });

        for (const filter of filters) {
            switch (filter.key) {
                case this.type + '.filter.order':
                    this.orderDate = filter.data;
                    this.filters = DataHelper.merge(this.filters, { order: this.orderDate });
                    break;
                case this.type + '.filter.slider':
                    const translate = this.slider.translate;
                    this.slider = filter.data;
                    this.slider.translate = translate;
                    this.tempSlider = DataHelper.clone(this.slider, null, true);
                    this.tempSlider.translate = this.slider.translate;
                    this.filters = DataHelper.merge(this.filters, { slider: this.slider });
                    break;
                case this.type + '.filter.dates':
                    this.dates = filter.data;
                    this.filters = DataHelper.merge(this.filters, { dates: this.dates });
                    break;
                default:
                    this.filters = DataHelper.merge(this.filters, { [filter.key.replace(this.type + '.filter.', '')]: filter.data });
            }
        }

        if (filters?.length > 0) {
            this.activeFilters = true;
            this.activeFiltersChange.emit(this.activeFilters);
            this.filtersChange.emit(this.filters);
        }
    }

    private initSlider(): void {
        this.slider = {
            minValue: 0,
            maxValue: 100,
            values: {
                min: 2,
                max: 24
            },
            step: 5,
            stepBottom: true,
            displayStepValue: true,
            customValues: [2, 4, 8, 16, 24],
            displayedValues: {
                2: '<2',
                4: '1/2',
                8: '1',
                16: '2',
                24: '3+'
            },
            translate: (value: string): string => {
                let translate = 'h';
                switch (value) {
                    case '1/2':
                    case '1':
                        translate = this.translateService.instant('words.day').toLowerCase();
                        break;
                    case '2':
                    case '3+':
                        translate = this.translateService.instant('words.days').toLowerCase();
                        break;
                }

                return value + ' ' + translate;
            }
        };
    }

    private setFilters(): void {
        this.filtersModal = [
            {
                name: 'langs',
                type: 'multiselect',
                label: this.translateService.instant('words.languages'),
                displayValue: this.availableLanguages.filter(lang => lang.isSelected).map(lang => lang.label).join(', '),
                options: this.availableLanguages
            },
            {
                name: 'providers',
                type: 'checkbox',
                label: this.translateService.instant('words.providers'),
                displayValue: this.translateService.instant('words.providers'),
                options: this.availableProviders
            },
            {
                name: 'skill',
                type: 'skill',
                label: this.translateService.instant('words.skill'),
                selected: { skill: null, level: [] },
                query: null,
                displayLimit: 12,
                options: this.availableSkills
            }
        ];

        // Inicializar openSections para todos los filtros dinámicos
        this.filtersModal.forEach(filter => {
            if (!this.openSections.hasOwnProperty(filter.name)) {
                this.openSections[filter.name] = true;
            }
        });
    }

    private storeFilters(): void {
        this.removeStoredFilters();
        for (const key in this.filters) {
            if (this.filters.hasOwnProperty(key)) {
                if (key !== 'skill') {
                    if (!DataHelper.isEmpty(this.filters[key])) {
                        this.storageService.setCache(this.type + '.filter.' + key, this.filters[key], 10);
                    }
                }
            }
        }
    }

    private removeStoredFilters(): void {
        const filters = this.storageService.getAllCache().filter((record: any) => {
            return record.key.match(this.type + '.filter.');
        });

        for (const filter of filters) {
            this.storageService.deleteCache(filter.key);
        }
    }

    getIterativeCheck(parent: any) {
        parent.children = parent.children.map((child: any) => {
            child.isSelected = parent.isSelected;
            return child;
        });
        return parent;
    }

    getSubTypesFilters(key: string) {
        const subTypes = this.availableTypes.find(t => t.value === key && t.isSelected);

        return subTypes ? subTypes.children.filter(t => t.isSelected).map(t => t.value) : [];
    }

    // ===== STATUS FILTER METHODS =====
    private initStatusOptions(): void {
        this.statusOptions = [
            {
                id: 'status-all',
                label: this.translateService.instant('filter.all') || 'All courses',
                value: 'all',
                isSelected: false,
                color: '#6b7280', // gray
                count: 0
            },
            {
                id: 'status-in-progress',
                label: this.translateService.instant('words.in-progress') || 'Ongoing',
                value: 'in_progress',
                isSelected: false,
                color: '#3b82f6', // blue
                count: 0
            },
            {
                id: 'status-completed',
                label: this.translateService.instant('words.completed') || 'Completed',
                value: 'completed',
                isSelected: false,
                color: '#10b981', // green
                count: 0
            }
        ];
    }

    public updateStatusCounts(counts: { inProgress: number; completed: number; notStarted: number; total: number }): void {
        this.statusCounts = counts;
        if (this.statusOptions.length > 0) {
            this.statusOptions[0].count = counts.total;       // all
            this.statusOptions[1].count = counts.inProgress;  // in_progress
            this.statusOptions[2].count = counts.completed;   // completed
        }
    }

    public onSelectStatus(option: any): void {
        if (option.value === 'all') {
            // "All courses" toggles: if selected, deselect all others; if deselected, just toggle off
            option.isSelected = !option.isSelected;
            if (option.isSelected) {
                // Deselect all specific statuses
                this.statusOptions.forEach(s => {
                    if (s.value !== 'all') { s.isSelected = false; }
                });
            }
        } else {
            option.isSelected = !option.isSelected;
            // Deselect "all" when any specific status is selected
            const allOption = this.statusOptions.find(s => s.value === 'all');
            if (allOption) { allOption.isSelected = false; }
        }
        this.saveFilters();
    }

    public get hasActiveStatusFilter(): boolean {
        return this.statusOptions.some(s => s.isSelected);
    }

    /** Radio-style selection from external pill tabs */
    public selectStatusByValue(value: string): void {
        if (value === 'all') {
            // Clear all status filters → show everything
            this.statusOptions = this.statusOptions.map(s => ({ ...s, isSelected: false }));
        } else {
            const wasSelected = this.statusOptions.find(s => s.value === value)?.isSelected;
            // Radio: select only this one, or deselect if clicking the same
            this.statusOptions = this.statusOptions.map(s => ({
                ...s,
                isSelected: s.value === value ? !wasSelected : false
            }));
        }
        this.saveFilters();
    }
    // ===== END STATUS FILTER METHODS =====

    saveFilters(): void {
        console.log('[TOPBAR] saveFilters() - contentTypes selected:', this.availableTypes.filter(t => t.isSelected).map(t => t.label));
        
        // FUSIÓN: external_content vive en availableTypes sincronizado con course,
        // así que filter() ya los incluye a ambos cuando "Cursos" está seleccionado.
        this.filters = DataHelper.merge(this.filters, { contentTypes: this.availableTypes.filter(t => t.isSelected).map(t => t.value) });
        this.filters = DataHelper.merge(this.filters, { courseTypes: this.getSubTypesFilters('course') });
        this.filters = DataHelper.merge(this.filters, { externalContentTypes: this.getSubTypesFilters('external_content') });
        this.filters = DataHelper.merge(this.filters, { externalResourceTypes: this.getSubTypesFilters('external_resource') });
        this.filters = DataHelper.merge(this.filters, { dates: this.dates });
        this.filters = DataHelper.merge(this.filters, { slider: this.slider });
        
        // Guardar proveedores
        const selectedProviders = this.availableProviders.filter(p => p.isChecked).map(p => p.value);
        this.filters = DataHelper.merge(this.filters, { providers: selectedProviders });

        // Guardar skills
        const selectedSkills = this.availableSkills.filter(s => s.isSelected).map(s => s.value);
        this.filters = DataHelper.merge(this.filters, { contentSkills: selectedSkills });

        // Guardar status filter
        const selectedStatuses = this.statusOptions.filter(s => s.isSelected).map(s => s.value);
        this.filters = DataHelper.merge(this.filters, { progressStatus: selectedStatuses });

        // Guardar filtro de inscripción (subscribed / not_subscribed / null)
        this.filters = DataHelper.merge(this.filters, { subscriptionFilter: this.subscriptionFilter });

        this.activeFilters = true;
        console.log('[TOPBAR] saveFilters() - emitting filtersChange:', this.filters);
        this.activeFiltersChange.emit(this.activeFilters);
        this.filtersChange.emit(this.filters);

        // Emitir evento para sincronizar el carrusel con los filtros del topbar
        this.emitterService.set('filter.sync.carousel', {
            availableTypes: this.availableTypes,
            availableProviders: this.availableProviders,
            availableSkills: this.availableSkills,
            timestamp: new Date().getTime()
        });

        this.storeFilters();
    }

    selectOrderDate(order: string): void {
        this.orderDate = order;

        this.activeFilters = true;
        this.activeFiltersChange.emit(this.activeFilters);
        this.filters = DataHelper.merge(this.filters, { order: this.orderDate });
        this.filtersChange.emit(this.filters);
        this.storeFilters();
    }

    onSelectOption(option) {
        this.selectOrderOptions = this.selectOrderOptions.map((selectDatum) => {
            selectDatum.isSelected = selectDatum.id === option.id;
            return selectDatum;
        });

        this.selectOrderDate(option.value);
    }

    getCheckboxType(type) {
        switch (true) {
            case type.children.every(subtype => subtype.isSelected):
                type.isSelected = true;
                return false;
            case type.children.some(subtype => subtype.isSelected):
                type.isSelected = true;
                return true;
            default:
                type.isSelected = false;
                return true;
        }
    }

    onSelectType(option) {
        console.log('[TOPBAR] onSelectType called:', option.label);
        option.isSelected = !option.isSelected;

        if (option.children) {
            this.getIterativeCheck(option);
        } else {
            const selectedType = this.availableTypes.find(type => {
                return type.children.find(subtype => subtype.id.includes(option.id));
            });
            if (selectedType) {
                selectedType.isSelected = selectedType.children.some(subtype => subtype.isSelected);
            }
        }

        // FUSIÓN: cuando se selecciona/deselecciona "Cursos", espejamos el mismo estado
        // en 'external_content' (incluyendo sus children) para que el backend reciba
        // ambos types y sus respectivos subtypes en una sola acción del usuario.
        if (option.value === 'course') {
            const extContent = this.availableTypes.find(t => t.value === 'external_content');
            if (extContent) {
                extContent.isSelected = option.isSelected;
                if (extContent.children) { this.getIterativeCheck(extContent); }
            }
        }

        // Ruta de Aprendizaje no se combina con proveedores: si se selecciona traject, limpiar proveedores
        if (option.value === 'traject' && option.isSelected) {
            this.availableProviders = this.availableProviders.map(provider => ({
                ...provider,
                isChecked: false,
                isSelected: false
            }));
            this.setFilters();
        }

        console.log('[TOPBAR] calling saveFilters()');
        this.saveFilters();
    }
/*
    resetDropdownFilters(): void {
        this.availableTypes = this.availableTypes.map(type => {
            type.isSelected = false
            type.children.map(subtype => {
                subtype.isSelected = false;
                return subtype;
            });
            return type;
        });

        this.selectOrderOptions = this.getSelectOrderOptions(FILTER_DATE_OPTIONS(this.translateService));
    }*/

    resetDropdownFilters(): void {
    // Reset tipos de contenido
    this.availableTypes = this.availableTypes.map(type => {
        type.isSelected = false;
        type.children = type.children.map(subtype => {
            subtype.isSelected = false;
            return subtype;
        });
        return type;
    });

    // Reset idiomas
    this.availableLanguages = this.availableLanguages.map(lang => {
        lang.isSelected = false;
        return lang;
    });

    // 3. Reset proveedores 
     this.availableProviders = this.availableProviders.map(provider => {
        return {
            ...provider,
            isChecked: false,  // Asegura resetear isChecked
            isSelected: false // Algunos componentes usan esta propiedad
        };
    });

    // Reset skills
    this.availableSkills = this.availableSkills.map(skill => {
        skill.isSelected = false;
        return skill;
    });

    // Reset ordenación
    this.selectOrderOptions = this.getSelectOrderOptions(FILTER_DATE_OPTIONS(this.translateService));

    // Reset status filter
    this.statusOptions = this.statusOptions.map(status => {
        status.isSelected = false;
        return status;
    });
}

    onFilterReset(): void {
        this.query = null;
        this.dates = null;
        this.filters = {};
        this.orderDate = '-date';
        this.activeFilters = false;
        this.subscriptionFilter = null;  // resetear filtro de inscripción
        this.activeFiltersChange.emit(this.activeFilters);
        this.filtersChange.emit(this.filters);
        this.initSlider();
        this.resetDropdownFilters();
        this.removeStoredFilters();
        this.setFilters();
        
        // Emitir evento para resetear los filtros del carrusel en list y watch
        this.emitterService.set('filter.reset.carousel', { timestamp: new Date().getTime() });
    }

    onFilterSave(data): void {
        this.filters = DataHelper.merge(this.filters, data);
        this.saveFilters();
    }

    onDurationOpen() {
        this.tempSlider = DataHelper.clone(this.slider, null, true);
        this.tempSlider.translate = this.slider.translate;
    }

    saveDuration(): void {
        this.slider = this.tempSlider;
        this.saveFilters();
    }

    search(query?): void {
        this.query = query;
        this.queryChange.emit(this.query);
    }

    changeMode(): void {
        this.mode = this.mode === 'list' ? 'calendar' : 'list';
        this.modeChange.emit(this.mode);
    }

    navigateSavedContent(): void {
        // Toggle icon state
        this.bookmarkActive = !this.bookmarkActive;
        this.router.navigate(['learn', this.company.slug, 'contents', 'saved']);
    }
  

    toggleExtension(): void {
        this.emitterService.toggleExtension.emit();
    }
}
